/*
 * Copyright 2025 Kridaprema
 */
package com.karyadina.ui.theme

import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.ui.graphics.Color

/**
 * Centralized flashcard color system to eliminate hardcoded colors.
 * All mastery states, backgrounds, and interactive elements use theme-aware colors.
 */
object FlashcardColors {

    // ============================================================================
    // MASTERY LEVEL COLORS - All states (NEW/LEARNING/REVIEW/MASTERED/FAMILIAR/KNOWN)
    // ============================================================================

    /**
     * Get the primary color for a mastery level.
     * Automatically adjusts for dark/light theme.
     */
    @Composable
    fun getMasteryColor(masteryLevel: String?): Color {
        val isDark = isSystemInDarkTheme()
        return when (masteryLevel?.uppercase()) {
            "NEW" -> if (isDark) StudyPalette.MasteryNewDark else StudyPalette.MasteryNew500
            "LEARNING" -> if (isDark) StudyPalette.MasteryLearningDark else StudyPalette.MasteryLearning500
            "REVIEW" -> if (isDark) StudyPalette.MasteryReviewDark else StudyPalette.MasteryReview500
            "MASTERED" -> if (isDark) StudyPalette.MasteryMasteredDark else StudyPalette.MasteryMastered500
            "FAMILIAR" -> if (isDark) StudyPalette.MasteryFamiliarDark else StudyPalette.MasteryFamiliar500
            "KNOWN" -> if (isDark) StudyPalette.MasteryKnownDark else StudyPalette.MasteryKnown500
            else -> MaterialTheme.colorScheme.secondary
        }
    }

    /**
     * Get the background color for a mastery badge.
     * Returns muted color suitable for badge backgrounds.
     */
    @Composable
    fun getMasteryBadgeBackgroundColor(masteryLevel: String?): Color {
        return when (masteryLevel?.uppercase()) {
            "NEW" -> StudyPalette.MasteryNew50
            "LEARNING" -> StudyPalette.MasteryLearning50
            "REVIEW" -> StudyPalette.MasteryReview50
            "MASTERED" -> StudyPalette.MasteryMastered50
            "FAMILIAR" -> StudyPalette.MasteryFamiliar50
            "KNOWN" -> StudyPalette.MasteryKnown50
            else -> MaterialTheme.colorScheme.surfaceVariant
        }
    }

    /**
     * Get the border/text color for a mastery badge.
     * Same as getMasteryColor - used for consistency.
     */
    @Composable
    fun getMasteryBadgeColor(masteryLevel: String?): Color {
        return getMasteryColor(masteryLevel)
    }

    // ============================================================================
    // CARD DISPLAY COLORS
    // ============================================================================

    /**
     * Get the background color for flashcard display (question card).
     */
    @Composable
    fun getQuestionCardBackgroundColor(): Color {
        return MaterialTheme.colorScheme.primaryContainer
    }

    /**
     * Get the background color for answer reveal card.
     */
    @Composable
    fun getAnswerCardBackgroundColor(): Color {
        return MaterialTheme.colorScheme.tertiaryContainer
    }

    /**
     * Get the text color for question text on card.
     */
    @Composable
    fun getQuestionTextColor(): Color {
        return MaterialTheme.colorScheme.onPrimaryContainer
    }

    /**
     * Get the text color for answer text on card.
     */
    @Composable
    fun getAnswerTextColor(): Color {
        return MaterialTheme.colorScheme.onTertiaryContainer
    }

    // ============================================================================
    // INTERACTIVE ELEMENT COLORS
    // ============================================================================

    /**
     * Get the color for the favorite/star icon.
     */
    @Composable
    fun getFavoriteColor(): Color {
        return Color(0xFFFFC107) // Amber/Gold (universal for favorites)
    }

    /**
     * Get the color for correct answer feedback.
     */
    @Composable
    fun getCorrectAnswerColor(): Color {
        return MaterialTheme.colorScheme.primary  // Use theme success/primary
    }

    /**
     * Get the color for incorrect answer feedback.
     */
    @Composable
    fun getIncorrectAnswerColor(): Color {
        return MaterialTheme.colorScheme.error
    }

    /**
     * Get the color for action buttons (review, study, etc.).
     */
    @Composable
    fun getActionButtonColor(): Color {
        return MaterialTheme.colorScheme.primary
    }

    /**
     * Get the color for delete/destructive actions.
     */
    @Composable
    fun getDeleteActionColor(): Color {
        return MaterialTheme.colorScheme.error
    }

    // ============================================================================
    // PROGRESS & STATISTICS COLORS
    // ============================================================================

    /**
     * Get the color for progress indicators.
     */
    @Composable
    fun getProgressIndicatorColor(): Color {
        return MaterialTheme.colorScheme.primary
    }

    /**
     * Get the color for progress indicators when due/overdue.
     */
    @Composable
    fun getProgressDueColor(): Color {
        return MaterialTheme.colorScheme.error
    }

    /**
     * Get the primary color for statistics cards.
     */
    @Composable
    fun getStatsBackgroundColor(): Color {
        return MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.3f)
    }

    /**
     * Get the color for stats values/numbers.
     */
    @Composable
    fun getStatsValueColor(): Color {
        return MaterialTheme.colorScheme.primary
    }

    // ============================================================================
    // STATUS INDICATOR COLORS
    // ============================================================================

    /**
     * Get color for "due for review" status badge.
     */
    @Composable
    fun getDueReviewColor(): Color {
        return MaterialTheme.colorScheme.error
    }

    /**
     * Get color for "all reviewed" / "caught up" status badge.
     */
    @Composable
    fun getReviewCompleteColor(): Color {
        return MaterialTheme.colorScheme.primary
    }

    /**
     * Get color for warning/notification indicators.
     */
    @Composable
    fun getWarningColor(): Color {
        return StudyColors.WarningLight
    }

    /**
     * Get background color for warning boxes/dialogs.
     */
    @Composable
    fun getWarningBackgroundColor(): Color {
        val isDark = isSystemInDarkTheme()
        return if (isDark) Color(0xFF2D0A0A) else StudyPalette.Warning50
    }

    // ============================================================================
    // TAG & CHIP COLORS (For formula, important, science tags, etc.)
    // ============================================================================

    /**
     * Get color for formula/math tags.
     */
    @Composable
    fun getFormulaTagColor(): Color {
        return StudyPalette.Blue500
    }

    /**
     * Get color for important/critical tags.
     */
    @Composable
    fun getImportantTagColor(): Color {
        return StudyPalette.Orange500
    }

    /**
     * Get color for science tags.
     */
    @Composable
    fun getScienceTagColor(): Color {
        return StudyPalette.Purple500
    }

    /**
     * Get color for history/geography tags.
     */
    @Composable
    fun getHistoryTagColor(): Color {
        return StudyPalette.Green500
    }

    /**
     * Get color for definition tags.
     */
    @Composable
    fun getDefinitionTagColor(): Color {
        return StudyPalette.Cyan500
    }

    /**
     * Get color for generic/default tags.
     */
    @Composable
    fun getDefaultTagColor(): Color {
        return MaterialTheme.colorScheme.primary
    }

    /**
     * Get tag color based on tag content/type.
     */
    @Composable
    fun getTagColor(tag: String): Color {
        val lowerTag = tag.lowercase()
        return when {
            lowerTag in listOf("formula", "equation", "math", "theorem", "proof") -> getFormulaTagColor()
            lowerTag in listOf("important", "critical", "key") -> getImportantTagColor()
            lowerTag in listOf("physics", "chemistry", "biology") -> getScienceTagColor()
            lowerTag in listOf("history", "geography", "social") -> getHistoryTagColor()
            lowerTag == "definition" -> getDefinitionTagColor()
            else -> getDefaultTagColor()
        }
    }

    // ============================================================================
    // ELEVATION & DEPTH COLORS (for shadows, backgrounds, etc.)
    // ============================================================================

    /**
     * Get the base elevation color for cards.
     */
    @Composable
    fun getElevationColor(elevation: Int = 2): Color {
        return MaterialTheme.colorScheme.surfaceVariant
    }

    /**
     * Get background color for dialogs with slight tint.
     */
    @Composable
    fun getDialogBackgroundColor(): Color {
        return MaterialTheme.colorScheme.surface
    }

    // ============================================================================
    // ANIMATION & TRANSITION COLORS
    // ============================================================================

    /**
     * Get the color for animation transition effects (used during card flip, etc.).
     */
    @Composable
    fun getAnimationAccentColor(): Color {
        return MaterialTheme.colorScheme.primary.copy(alpha = 0.5f)
    }

    /**
     * Get the color for celebration/success animation.
     */
    @Composable
    fun getCelebrationColor(): Color {
        return getCorrectAnswerColor()
    }
}
